import { useState, useEffect } from 'react';
import { formatBusinessNumber, formatPhoneNumber } from '../../../../../../utils/format';

type CategoryOption = {
  vendorCategoryIdx: number;
  name?: string;
  label?: string;
  orderNo?: number;
  children?: CategoryOption[];
};

export type VendorEditData = {
  name: string;
  status: string;
  businessNumber: string;
  vendorCategoryIdx: number | null;
  managerName: string;
  email: string;
  contact: string;
  notiPhone: string;
  address: string;
  commissionRate: number;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  memo: string;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialData: any;
  onSave: (data: VendorEditData) => Promise<void>;
}

const toPositiveNumberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export default function VendorEditModal({ isOpen, onClose, initialData, onSave }: Props) {
  const [categoryTree, setCategoryTree] = useState<CategoryOption[]>([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [selectedParentIdx, setSelectedParentIdx] = useState<number | null>(null);

  const [formData, setFormData] = useState<VendorEditData>({
    name: '',
    status: 'ACTIVE',
    businessNumber: '',
    vendorCategoryIdx: null,
    managerName: '',
    email: '',
    contact: '',
    notiPhone: '',
    address: '',
    commissionRate: 0,
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    memo: ''
  });

  useEffect(() => {
    if (!isOpen) return;

    const fetchCategories = async () => {
      setIsCategoryLoading(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch('https://api.wedmin.biz/vendor/category', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('카테고리 조회 실패');

        const payload = await response.json();
        const tree: CategoryOption[] = Array.isArray(payload)
          ? payload
          : payload?.status
            ? payload.data
            : [];

        const sortedTree = [...tree].sort(
          (a, b) => (a.orderNo ?? Number.MAX_SAFE_INTEGER) - (b.orderNo ?? Number.MAX_SAFE_INTEGER)
        );

        sortedTree.forEach((parent) => {
          if (parent.children) {
            parent.children = [...parent.children].sort(
              (a, b) => (a.orderNo ?? Number.MAX_SAFE_INTEGER) - (b.orderNo ?? Number.MAX_SAFE_INTEGER)
            );
          }
        });
        setCategoryTree(sortedTree);
      } catch (error) {
        console.error('Fetch category options error:', error);
        setCategoryTree([]);
      } finally {
        setIsCategoryLoading(false);
      }
    };

    fetchCategories();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && initialData) {
      const categoryIdx =
        toPositiveNumberOrNull(initialData.vendorCategoryIdx) ||
        toPositiveNumberOrNull(initialData.category?.vendorCategoryIdx);

      setFormData({
        name: initialData.name || '',
        status: initialData.status || 'ACTIVE',
        businessNumber: initialData.businessNumber || '',
        vendorCategoryIdx: categoryIdx,
        managerName: initialData.managerName || '',
        email: initialData.email || '',
        contact: initialData.contact || '',
        notiPhone: initialData.notiPhone || '',
        address: initialData.address || '',
        commissionRate: Number(initialData.commissionRate || 0),
        bankName: initialData.bankName || '',
        accountNumber: initialData.accountNumber || '',
        accountHolder: initialData.accountHolder || '',
        memo: initialData.memo || ''
      });
    }
  }, [isOpen, initialData]);

  // categoryTree가 로드된 후, initialData의 categoryIdx로 parentIdx를 찾아 설정
  useEffect(() => {
    if (categoryTree.length === 0 || !formData.vendorCategoryIdx) {
      setSelectedParentIdx(null);
      return;
    }
    // 현재 선택된 카테고리가 대분류인지 확인
    const asParent = categoryTree.find((p) => p.vendorCategoryIdx === formData.vendorCategoryIdx);
    if (asParent) {
      setSelectedParentIdx(asParent.vendorCategoryIdx);
      return;
    }
    // 소분류인 경우 부모를 찾음
    for (const parent of categoryTree) {
      const match = parent.children?.find((c) => c.vendorCategoryIdx === formData.vendorCategoryIdx);
      if (match) {
        setSelectedParentIdx(parent.vendorCategoryIdx);
        return;
      }
    }
    setSelectedParentIdx(null);
  }, [categoryTree, formData.vendorCategoryIdx]);

  const selectedParent = categoryTree.find((p) => p.vendorCategoryIdx === selectedParentIdx);
  const subCategoryOptions = selectedParent?.children || [];

  const hasSelectedCategory = (() => {
    if (!formData.vendorCategoryIdx || categoryTree.length === 0) return false;
    for (const parent of categoryTree) {
      if (parent.vendorCategoryIdx === formData.vendorCategoryIdx) return true;
      if (parent.children?.some((c) => c.vendorCategoryIdx === formData.vendorCategoryIdx)) return true;
    }
    return false;
  })();

  const requiresReselection = categoryTree.length > 0 && !hasSelectedCategory;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === 'commissionRate'
          ? Number(value)
          : name === 'vendorCategoryIdx'
            ? (value ? Number(value) : null)
            : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (categoryTree.length > 0 && !formData.vendorCategoryIdx) {
      alert('카테고리를 선택해주세요.');
      return;
    }
    const payload: any = { ...formData };
    if (!payload.notiPhone?.trim()) {
      delete payload.notiPhone;
    } else {
      payload.notiPhone = formatPhoneNumber(payload.notiPhone);
    }
    if (!payload.email?.trim()) delete payload.email;
    if (!payload.businessNumber?.trim()) delete payload.businessNumber;
    if (!payload.address?.trim()) delete payload.address;
    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">업체 정보 수정</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 bg-gray-50 p-2 rounded">기본 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">업체명</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                <select name="status" value={formData.status} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="ACTIVE">활성 (ACTIVE)</option>
                  <option value="DEACTIVE">비활성 (DEACTIVE)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">대분류</label>
                <select
                  value={selectedParentIdx ?? ''}
                  onChange={(e) => {
                    const parentIdx = e.target.value ? Number(e.target.value) : null;
                    setSelectedParentIdx(parentIdx);
                    if (parentIdx) {
                      const parent = categoryTree.find((p) => p.vendorCategoryIdx === parentIdx);
                      const firstChild = parent?.children?.[0];
                      setFormData((prev) => ({
                        ...prev,
                        vendorCategoryIdx: firstChild ? firstChild.vendorCategoryIdx : parentIdx
                      }));
                    } else {
                      setFormData((prev) => ({ ...prev, vendorCategoryIdx: null }));
                    }
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  disabled={isCategoryLoading}
                >
                  <option value="">
                    {isCategoryLoading ? '불러오는 중...' : '대분류 선택'}
                  </option>
                  {categoryTree.map((cat) => (
                    <option key={cat.vendorCategoryIdx} value={cat.vendorCategoryIdx}>
                      {cat.label || cat.name}
                    </option>
                  ))}
                </select>
                {requiresReselection && (
                  <p className="mt-1 text-xs text-amber-600">
                    현재 카테고리가 미지정이거나 reset 후 유효하지 않습니다. 다시 선택해주세요.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">소분류</label>
                <select
                  value={
                    subCategoryOptions.some((c) => c.vendorCategoryIdx === formData.vendorCategoryIdx)
                      ? formData.vendorCategoryIdx ?? ''
                      : ''
                  }
                  onChange={(e) => {
                    const childIdx = e.target.value ? Number(e.target.value) : null;
                    setFormData((prev) => ({
                      ...prev,
                      vendorCategoryIdx: childIdx || selectedParentIdx
                    }));
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  disabled={isCategoryLoading || !selectedParentIdx || subCategoryOptions.length === 0}
                >
                  {subCategoryOptions.length === 0 ? (
                    <option value="">소분류 없음</option>
                  ) : (
                    <>
                      <option value="">소분류 선택</option>
                      {subCategoryOptions.map((cat) => (
                        <option key={cat.vendorCategoryIdx} value={cat.vendorCategoryIdx}>
                          {cat.label || cat.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">사업자번호</label>
                <input type="text" name="businessNumber" value={formatBusinessNumber(formData.businessNumber)} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 bg-gray-50 p-2 rounded">담당자 및 연락처</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">담당자명</label>
                <input type="text" name="managerName" value={formData.managerName} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">대표 연락처</label>
                <input type="text" name="contact" value={formData.contact} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="예: 02-1234-5678" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">알림톡 수신번호</label>
                <input type="tel" name="notiPhone" value={formatPhoneNumber(formData.notiPhone)}
                  onChange={handleChange}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 11);
                    setFormData((prev) => ({ ...prev, notiPhone: pasted }));
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="010-0000-0000" maxLength={13} />
                <p className="mt-1 text-xs text-gray-500">010 휴대폰 번호만 입력 가능</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 bg-gray-50 p-2 rounded">정산 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">수수료율 (%)</label>
                <input type="number" name="commissionRate" value={formData.commissionRate} onChange={handleChange} step="0.1" className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">은행명</label>
                <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">계좌번호</label>
                <input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">예금주</label>
                <input type="text" name="accountHolder" value={formData.accountHolder} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
            <textarea name="memo" value={formData.memo} onChange={handleChange} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">취소</button>
            <button type="submit" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">수정 완료</button>
          </div>
        </form>
      </div>
    </div>
  );
}
